export const PENDING_INVITE_TOKEN_KEY = "pending-invite-token";
export const PENDING_INVITE_EMAIL_KEY = "pending-invite-email";
export const PENDING_INVITE_AUTH_METHOD_KEY = "pending-invite-auth-method";

export type InviteAuthMethod = "google" | "password" | "magic";

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type ResolveInviteActionInput = {
  authLoading: boolean;
  userEmail?: string | null;
  inviteEmail: string;
  needsPasswordSetup: boolean;
};

export type InviteAction =
  | "wait"
  | "sign_in"
  | "email_mismatch"
  | "choose_method"
  | "auto_accept";

export type PendingInviteAuth = {
  token: string;
  email: string;
  method: InviteAuthMethod | null;
};

const normalizeEmail = (value?: string | null) => value?.trim().toLowerCase() || "";

export function getInviteEmailFromSearch(search: string) {
  return normalizeEmail(new URLSearchParams(search).get("email"));
}

export function resolveInviteAction({
  authLoading,
  userEmail,
  inviteEmail,
  needsPasswordSetup,
}: ResolveInviteActionInput): InviteAction {
  if (authLoading) return "wait";

  const normalizedUserEmail = normalizeEmail(userEmail);
  if (!normalizedUserEmail) return "sign_in";
  if (normalizedUserEmail !== normalizeEmail(inviteEmail)) return "email_mismatch";
  if (needsPasswordSetup) return "choose_method";
  return "auto_accept";
}

export function savePendingInviteAuth(
  storage: StorageLike,
  pending: { token: string; email: string; method: InviteAuthMethod }
) {
  storage.setItem(PENDING_INVITE_TOKEN_KEY, pending.token);
  storage.setItem(PENDING_INVITE_EMAIL_KEY, normalizeEmail(pending.email));
  storage.setItem(PENDING_INVITE_AUTH_METHOD_KEY, pending.method);
}

export function readPendingInviteAuth(storage: StorageLike): PendingInviteAuth | null {
  const token = storage.getItem(PENDING_INVITE_TOKEN_KEY)?.trim() || "";
  if (!token) return null;

  const rawMethod = storage.getItem(PENDING_INVITE_AUTH_METHOD_KEY);
  const method = rawMethod === "google" || rawMethod === "password" || rawMethod === "magic" ? rawMethod : null;

  return {
    token,
    email: normalizeEmail(storage.getItem(PENDING_INVITE_EMAIL_KEY)),
    method,
  };
}

export function clearPendingInviteAuthChoice(storage: StorageLike) {
  storage.removeItem(PENDING_INVITE_EMAIL_KEY);
  storage.removeItem(PENDING_INVITE_AUTH_METHOD_KEY);
}

export function clearPendingInviteAuth(storage: StorageLike) {
  storage.removeItem(PENDING_INVITE_TOKEN_KEY);
  clearPendingInviteAuthChoice(storage);
}

export function isExpectedInviteEmail(actualEmail: string | null | undefined, expectedEmail: string) {
  return normalizeEmail(actualEmail) === normalizeEmail(expectedEmail);
}

export function isAlreadyAcceptedInviteError(message?: string | null) {
  return message?.trim().toLowerCase().includes("invitation has already been accepted") === true;
}

export function hasGoogleProvider(appMetadata: unknown) {
  if (!appMetadata || typeof appMetadata !== "object") return false;

  const metadata = appMetadata as { provider?: unknown; providers?: unknown };
  if (metadata.provider === "google") return true;
  return Array.isArray(metadata.providers) && metadata.providers.includes("google");
}

export function shouldEnterWorkspaceAfterMemberInvite({
  hasOrganizations,
  pendingInviteCount,
  onboardingMode,
  needsPasswordSetup,
}: {
  hasOrganizations: boolean;
  pendingInviteCount: number;
  onboardingMode?: string | null;
  needsPasswordSetup: boolean;
}) {
  return (
    hasOrganizations &&
    pendingInviteCount === 0 &&
    onboardingMode === "member_setup" &&
    !needsPasswordSetup
  );
}

/** The API interceptor rejects with the response body, not an AxiosError. */
export function getInviteAuthErrorMessage(error: unknown): string {
  if (!error || typeof error !== "object") return "";
  const value = error as { error?: unknown; message?: unknown; response?: { data?: { error?: unknown } } };
  const message = value.response?.data?.error ?? value.error ?? value.message;
  return typeof message === "string" ? message : "";
}
